/**
 * The settings frame (mission 6 WP05 T033; FR-003, C-013; research §1.5; style
 * guide §5.1, §8 "Settings"; sheet 40 callout 1).
 *
 * A 200 px sub-nav, a 32 px gap, then a main column of at most 880 px holding
 * the screen, which passes its own `h1`. No `<main>` here: the shell owns the
 * one `main`. The sub-nav lists the sections of this release: Users & roles
 * (one identity per tenant, C-008) is absent, not disabled (C-013). Mission 7
 * WP08 (T046; FR-025; sheet 41 callout 1) adds Bureau credentials; mission 9
 * WP13 (T078; FR-014; sheet 50 callout 1) adds Billing. Mission 10 WP15 (T089;
 * FR-017, FR-018; sheets 43 and 44 callout 1; OR-15, U15) adds Data retention
 * and Audit log in the drawn order (wireframes index.md:115): Organization,
 * Header parameters, Bureau credentials, Data retention, Billing, Audit log.
 * WP16 and WP17 built their routes (sheets 43 and 44). The labels are the
 * register 17 crumbs, so the sub-nav and the breadcrumb always agree.
 * `NavLink … end` is right here: the paths are siblings, not prefixes.
 */
import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'

import { cn } from '@/components/ui/cn'
import { copyText } from '@/lib/provisional-copy'

const SETTINGS_SECTIONS = [
  { to: '/app/settings/organization', label: copyText('crumb.organization') },
  { to: '/app/settings/header', label: copyText('crumb.headerParameters') },
  { to: '/app/settings/bureaus', label: copyText('crumb.bureaus') },
  { to: '/app/settings/data', label: copyText('crumb.data') },
  { to: '/app/settings/billing', label: copyText('crumb.billing') },
  { to: '/app/settings/audit', label: copyText('crumb.audit') },
] as const

export function SettingsLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-8">
      <nav aria-label="Settings" className="w-[var(--settings-subnav-width)] shrink-0">
        <p className="px-3 text-overline uppercase text-navy-600">{copyText('crumb.settings')}</p>
        <ul className="mt-3 flex flex-col gap-1">
          {SETTINGS_SECTIONS.map((s) => (
            <li key={s.to}>
              <NavLink
                to={s.to}
                end
                className={({ isActive }) =>
                  cn(
                    'flex h-9 items-center rounded-md px-3 text-body-sm font-medium text-navy-700 hover:bg-navy-100 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]',
                    isActive &&
                      'bg-[var(--color-accent-50)] text-navy-950 [box-shadow:inset_3px_0_0_var(--color-accent)] hover:bg-[var(--color-accent-50)]',
                  )
                }
              >
                {s.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="flex min-w-0 max-w-[880px] flex-1 flex-col gap-4">{children}</div>
    </div>
  )
}
